/**
 * Phrasing helpers for the report copy. Every number and particle in the
 * report goes through here so formatting stays consistent with the UI.
 */
import {
  dayKey,
  dayKeyToMs,
  finalConsonant,
  formatCount,
  formatDate,
  formatDuration,
  formatHour,
  formatMonthDay,
  formatPercent,
  formatTime,
  josa,
  wallClock,
  type JosaPair,
} from '../format';
import { isFiniteNumber } from './metrics';

export { formatCount as count, formatDuration as duration, formatHour as hourText, formatPercent as percent, josa };

/** 서진은 / 민지는 */
export function topic(word: string): string {
  return josa(word, '은/는');
}

/** 서진이 / 민지가 */
export function subject(word: string): string {
  return josa(word, '이/가');
}

/** ‘봐서’ */
export function quoted(phrase: string): string {
  return `‘${phrase}’`;
}

/** True when josa() can pick a particle without the "이(가)" fallback. */
export function takesParticle(word: string): boolean {
  return finalConsonant(word) !== null;
}

/**
 * josa() that also reads a trailing "%" as 퍼센트: "73%가", "10%~14%예요",
 * while "1% 미만" follows the usual rule ("1% 미만이").
 */
export function attach(text: string, pair: JosaPair): string {
  if (!text.endsWith('%')) return josa(text, pair);
  return text + (pair.split('/')[1] ?? '');
}

/** formatPercent plus particle: percentWith(0.73, '이/가') -> "73%가". */
export function percentWith(rate: number, pair: JosaPair): string {
  return attach(formatPercent(rate), pair);
}

/** 4.25 -> "4.3", 4 -> "4", 1234 -> "1,234" */
export function decimal(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? formatCount(rounded) : rounded.toFixed(1);
}

/** Whole numbers from 10 up, one decimal below: 12.6 -> "13", 3.25 -> "3.3". */
export function looseNumber(value: number): string {
  return value >= 10 ? formatCount(value) : decimal(value);
}

/** 2.44 -> "2.4배", 12.6 -> "13배" */
export function timesText(ratio: number): string {
  return `${looseNumber(ratio)}배`;
}

/** "1분 안에" for sub-minute delays, otherwise "2시간 40분 만에". */
export function withinText(minutes: number): string {
  if (!isFiniteNumber(minutes) || minutes < 1) return '1분 안에';
  return `${formatDuration(minutes)} 만에`;
}

/** Count per 100 messages: 0 -> "0개", 0.003 -> "1개 미만", 0.184 -> "18개". */
export function per100Text(rate: number): string {
  const value = rate * 100;
  if (!(value > 0)) return '0개';
  if (value < 0.5) return '1개 미만';
  return `${formatCount(value)}개`;
}

/** 12.4 -> "12자" */
export function charsText(value: number): string {
  return `${formatCount(value)}자`;
}

export function dateText(ms: number): string | null {
  return isFiniteNumber(ms) ? formatDate(ms) : null;
}

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** "2024-03-01" -> wall-clock ms, or null when the key is malformed. */
export function dayKeyMs(key: unknown): number | null {
  if (typeof key !== 'string' || !DAY_KEY.test(key)) return null;
  const ms = dayKeyToMs(key);
  return isFiniteNumber(ms) ? ms : null;
}

/**
 * "2024년 3월 1일부터 4월 2일까지"; the year repeats only when it changes,
 * and a same-day span shows times: "2024년 3월 1일 오후 1:00부터 오후 6:00까지".
 */
export function periodText(fromMs: number, toMs: number, withTimes = true): string {
  if (withTimes && dayKey(fromMs) === dayKey(toMs)) {
    return `${formatDate(fromMs)} ${formatTime(fromMs)}부터 ${formatTime(toMs)}까지`;
  }
  const end = wallClock(fromMs).year === wallClock(toMs).year ? formatMonthDay(toMs) : formatDate(toMs);
  return `${formatDate(fromMs)}부터 ${end}까지`;
}

/** "2분~5분" or just "2분" when both ends read the same. */
export function spanText(low: string, high: string): string {
  return low === high ? low : `${low}~${high}`;
}
